import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link2, Loader2, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { FieldLabel } from "@/admin/ui";
import { useAdminCategories, useMeta } from "@/admin/hooks";
import { RunProgress } from "@/admin/RunProgress";

const INITIAL = { mode: "topic", topic: "", url: "", tone: "default", category: "auto", generate_image: true, publish: false };

const ModeButton = ({ active, onClick, icon: Icon, title, text, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-left border-2 p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}>
    <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
    <p className="font-semibold mt-2">{title}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{text}</p>
  </button>
);

export const GenerateOptions = ({ form, set, meta, cats }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div>
      <FieldLabel>Tono</FieldLabel>
      <Select value={form.tone || "default"} onValueChange={set("tone")}>
        <SelectTrigger data-testid="generate-tone-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="default">El tono por defecto</SelectItem>
          {meta?.tones.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <FieldLabel>Sección</FieldLabel>
      <Select value={form.category || "auto"} onValueChange={set("category")}>
        <SelectTrigger data-testid="generate-category-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Que la elija la IA</SelectItem>
          {cats?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export const GenerateDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [runId, setRunId] = useState(null);
  const { data: meta } = useMeta();
  const { data: cats } = useAdminCategories();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = useMutation({
    mutationFn: () => api.post("/admin/generate", { ...form, tone: form.tone === "default" ? null : form.tone, category: form.category === "auto" ? null : form.category }).then((r) => r.data),
    onSuccess: (run) => setRunId(run.id),
    onError: (e) => toast.error(errMsg(e)),
  });
  const reset = () => { setRunId(null); setForm(INITIAL); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="generate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold" /> Generar noticia con IA</DialogTitle>
          <DialogDescription>Elige de dónde parte la noticia. La IA la redacta con el estilo de la casa, sus claves, preguntas frecuentes y SEO.</DialogDescription>
        </DialogHeader>
        {runId ? (
          <div className="space-y-4">
            <RunProgress runId={runId} onOpen={() => setOpen(false)} />
            <Button variant="outline" onClick={reset} data-testid="generate-another-btn">Generar otra</Button>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}>
            <div className="grid sm:grid-cols-2 gap-3">
              <ModeButton active={form.mode === "topic"} onClick={() => set("mode")("topic")} icon={PenLine} title="Sobre un tema" text="Artículo original de análisis o guía" testid="generate-mode-topic" />
              <ModeButton active={form.mode === "url"} onClick={() => set("mode")("url")} icon={Link2} title="Desde una noticia" text="Pega un enlace y la reescribe a tu estilo" testid="generate-mode-url" />
            </div>
            {form.mode === "topic" ? (
              <div>
                <FieldLabel>¿Sobre qué quieres escribir?</FieldLabel>
                <Textarea rows={3} value={form.topic} onChange={(e) => set("topic")(e.target.value)} placeholder="Ej.: Cómo afecta la subida de tipos a la financiación de las pymes" data-testid="generate-topic-input" />
              </div>
            ) : (
              <div>
                <FieldLabel>Enlace de la noticia original</FieldLabel>
                <Input value={form.url} onChange={(e) => set("url")(e.target.value)} placeholder="https://…" data-testid="generate-url-input" />
              </div>
            )}
            <GenerateOptions form={form} set={set} meta={meta} cats={cats} />
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-center justify-between gap-3 border border-border p-3 text-sm">Crear imagen con IA<Switch checked={form.generate_image} onCheckedChange={set("generate_image")} data-testid="generate-image-switch" /></label>
              <label className="flex items-center justify-between gap-3 border border-border p-3 text-sm">Publicar directamente<Switch checked={form.publish} onCheckedChange={set("publish")} data-testid="generate-publish-switch" /></label>
            </div>
            <Button type="submit" className="w-full h-11" disabled={submit.isPending} data-testid="generate-submit-btn">
              {submit.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} Generar noticia
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
